package com.mgacreative.hotel.feature.staff.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowLeft
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotel.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import org.koin.compose.viewmodel.koinViewModel

data class ShiftItem(
    val id: String,
    val employeeName: String,
    val role: String,
    val time: String,
    val isPresent: Boolean
)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ShiftPlanningScreen(
    viewModel: ShiftPlannerViewModel = koinViewModel(),
    onNavigateBack: () -> Unit = {},
    onNavigate: (String) -> Unit
) {
    val shifts by viewModel.shifts.collectAsState()
    val staffList by viewModel.staffList.collectAsState()
    val currentDate by viewModel.currentDate.collectAsState()

    var showAddDialog by remember { mutableStateOf(false) }
    var selectedStaffId by remember { mutableStateOf("") }
    var startTime by remember { mutableStateOf("08:00") }
    var endTime by remember { mutableStateOf("16:00") }
    var role by remember { mutableStateOf("Resepsiyon") }

    val mappedShifts = shifts.map { shift ->
        val staff = staffList.find { it.id == shift.staff_id }
        ShiftItem(
            id = shift.id,
            employeeName = staff?.full_name ?: staff?.department ?: "Personel",
            role = shift.role ?: "Personel",
            time = "${shift.start_time.take(5)} - ${shift.end_time.take(5)}",
            isPresent = shift.status == "IN_PROGRESS" || shift.status == "SCHEDULED"
        )
    }

    val today = remember { Clock.System.todayIn(TimeZone.currentSystemDefault()) }
    val isToday = currentDate == today

    Scaffold(
        topBar = {
            Surface(
                color = Color.White,
                border = BorderStroke(1.dp, Color(0xFFE2E8F0))
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 8.dp, vertical = 8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    IconButton(onClick = onNavigateBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = l10n("Geri"), tint = Color(0xFF0F172A))
                    }
                    Spacer(Modifier.width(4.dp))
                    Column {
                        Text(
                            text = l10n("Vardiya Planı"),
                            fontWeight = FontWeight.Bold,
                            fontSize = 18.sp,
                            color = Color(0xFF0F172A)
                        )
                        Text(
                            text = "Personel çalışma saatleri ve nöbet takvimi",
                            fontSize = 12.sp,
                            color = Color(0xFF64748B)
                        )
                    }
                }
            }
        },
        contentWindowInsets = WindowInsets(0, 0, 0, 0),
        containerColor = Color(0xFFF8FAFC),
        floatingActionButton = {
            FloatingActionButton(
                onClick = { showAddDialog = true },
                containerColor = Color(0xFF2563EB),
                contentColor = Color.White,
                shape = RoundedCornerShape(14.dp)
            ) {
                Row(
                    modifier = Modifier.padding(horizontal = 16.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(20.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("Vardiya Ekle", fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
                }
            }
        },
        bottomBar = {
            ShiftBottomNavBar(
                selectedTab = 0,
                onShiftsClick = { /* Zaten buradayız */ },
                onPersonelClick = { onNavigate("StaffListScreen") }
            )
        }
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .padding(16.dp)
        ) {
            // Modern Date Navigator Card
            Card(
                shape = RoundedCornerShape(16.dp),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(12.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Surface(
                            shape = RoundedCornerShape(10.dp),
                            color = Color(0xFFF1F5F9),
                            modifier = Modifier.size(38.dp)
                        ) {
                            IconButton(onClick = { viewModel.previousDay() }) {
                                Icon(Icons.AutoMirrored.Filled.KeyboardArrowLeft, contentDescription = "Önceki", tint = Color(0xFF334155))
                            }
                        }

                        Spacer(Modifier.width(12.dp))

                        Column {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(Icons.Outlined.CalendarMonth, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(16.dp))
                                Spacer(Modifier.width(6.dp))
                                Text(
                                    text = currentDate.toString(),
                                    fontWeight = FontWeight.Bold,
                                    fontSize = 16.sp,
                                    color = Color(0xFF0F172A)
                                )
                            }
                            Text(
                                text = if (isToday) "Bugünün Vardiyaları" else "Planlanan Gün",
                                fontSize = 12.sp,
                                color = if (isToday) Color(0xFF2563EB) else Color(0xFF64748B),
                                fontWeight = if (isToday) FontWeight.SemiBold else FontWeight.Normal
                            )
                        }
                    }

                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Surface(
                            shape = RoundedCornerShape(8.dp),
                            color = Color(0xFFEFF6FF),
                            border = BorderStroke(1.dp, Color(0xFFDBEAFE))
                        ) {
                            Text(
                                text = "${mappedShifts.size} Personel",
                                fontSize = 12.sp,
                                fontWeight = FontWeight.SemiBold,
                                color = Color(0xFF2563EB),
                                modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp)
                            )
                        }

                        Spacer(Modifier.width(10.dp))

                        Surface(
                            shape = RoundedCornerShape(10.dp),
                            color = Color(0xFFF1F5F9),
                            modifier = Modifier.size(38.dp)
                        ) {
                            IconButton(onClick = { viewModel.nextDay() }) {
                                Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, contentDescription = "Sonraki", tint = Color(0xFF334155))
                            }
                        }
                    }
                }
            }

            Spacer(Modifier.height(16.dp))

            // Shifts List
            val shiftListState = androidx.compose.foundation.lazy.rememberLazyListState()
            LazyColumn(
                state = shiftListState,
                modifier = Modifier.fillMaxSize().verticalScrollbar(shiftListState),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                if (mappedShifts.isEmpty()) {
                    item {
                        Card(
                            shape = RoundedCornerShape(16.dp),
                            colors = CardDefaults.cardColors(containerColor = Color.White),
                            border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(40.dp),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                Surface(
                                    shape = CircleShape,
                                    color = Color(0xFFF8FAFC),
                                    modifier = Modifier.size(56.dp)
                                ) {
                                    Box(contentAlignment = Alignment.Center) {
                                        Icon(Icons.Outlined.Schedule, contentDescription = null, tint = Color(0xFF94A3B8), modifier = Modifier.size(28.dp))
                                    }
                                }
                                Spacer(Modifier.height(12.dp))
                                Text(
                                    text = "Bu tarihe ait vardiya planı bulunmuyor",
                                    fontWeight = FontWeight.Bold,
                                    fontSize = 15.sp,
                                    color = Color(0xFF334155)
                                )
                                Spacer(Modifier.height(4.dp))
                                Text(
                                    text = "Yeni vardiya eklemek için aşağıdaki butonu kullanabilirsiniz.",
                                    fontSize = 13.sp,
                                    color = Color(0xFF64748B)
                                )
                                Spacer(Modifier.height(16.dp))
                                OutlinedButton(
                                    onClick = { showAddDialog = true },
                                    shape = RoundedCornerShape(10.dp),
                                    border = BorderStroke(1.dp, Color(0xFF2563EB))
                                ) {
                                    Icon(Icons.Default.Add, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(16.dp))
                                    Spacer(Modifier.width(6.dp))
                                    Text("İlk Vardiyayı Ekle", color = Color(0xFF2563EB), fontWeight = FontWeight.SemiBold)
                                }
                            }
                        }
                    }
                } else {
                    items(mappedShifts) { shift ->
                        ShiftModernCard(shift)
                    }
                }
                item { Spacer(modifier = Modifier.height(72.dp)) }
            }
        }
    }

    // Modern Add Shift Modal Dialog
    if (showAddDialog) {
        val quickRoles = listOf("Ön Büro / Resepsiyon", "Kat Hizmetleri", "Restoran & Bar", "Güvenlik", "Teknik Servis", "Mutfak")
        val quickShifts = listOf(
            Triple("Sabah", "08:00", "16:00"),
            Triple("Akşam", "16:00", "00:00"),
            Triple("Gece", "00:00", "08:00")
        )

        AlertDialog(
            onDismissRequest = { showAddDialog = false },
            containerColor = Color.White,
            shape = RoundedCornerShape(20.dp),
            title = {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Surface(
                        shape = RoundedCornerShape(10.dp),
                        color = Color(0xFFEFF6FF),
                        modifier = Modifier.size(40.dp)
                    ) {
                        Box(contentAlignment = Alignment.Center) {
                            Icon(Icons.Outlined.AccessTime, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(22.dp))
                        }
                    }
                    Spacer(Modifier.width(12.dp))
                    Column {
                        Text(l10n("Yeni Vardiya Planla"), fontWeight = FontWeight.Bold, fontSize = 18.sp, color = Color(0xFF0F172A))
                        Text("Tarih: $currentDate", fontSize = 12.sp, color = Color(0xFF64748B))
                    }
                }
            },
            text = {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(14.dp)
                ) {
                    // Staff Selector Dropdown
                    var expanded by remember { mutableStateOf(false) }
                    Column {
                        Text("Personel Seçimi", fontSize = 12.sp, fontWeight = FontWeight.SemiBold, color = Color(0xFF475569))
                        Spacer(Modifier.height(6.dp))
                        Box(modifier = Modifier.fillMaxWidth()) {
                            OutlinedCard(
                                onClick = { expanded = true },
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(10.dp),
                                colors = CardDefaults.outlinedCardColors(containerColor = Color(0xFFF8FAFC)),
                                border = BorderStroke(1.dp, Color(0xFFCBD5E1))
                            ) {
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(horizontal = 14.dp, vertical = 12.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    val selectedStaff = staffList.find { it.id == selectedStaffId }
                                    Text(
                                        text = selectedStaff?.full_name ?: "Personel seçiniz...",
                                        color = if (selectedStaffId.isEmpty()) Color(0xFF94A3B8) else Color(0xFF0F172A),
                                        fontWeight = if (selectedStaffId.isEmpty()) FontWeight.Normal else FontWeight.SemiBold,
                                        fontSize = 14.sp
                                    )
                                    Icon(Icons.Default.ArrowDropDown, contentDescription = null, tint = Color(0xFF64748B))
                                }
                            }
                            DropdownMenu(
                                expanded = expanded,
                                onDismissRequest = { expanded = false },
                                modifier = Modifier.fillMaxWidth(0.85f).background(Color.White)
                            ) {
                                if (staffList.isEmpty()) {
                                    DropdownMenuItem(
                                        text = { Text("Kayıtlı personel bulunamadı", color = Color.Gray) },
                                        onClick = { expanded = false }
                                    )
                                } else {
                                    staffList.forEach { staff ->
                                        DropdownMenuItem(
                                            text = {
                                                Column {
                                                    Text(staff.full_name, fontWeight = FontWeight.SemiBold, color = Color(0xFF0F172A))
                                                    if (!staff.department.isNullOrBlank()) {
                                                        Text(staff.department, fontSize = 11.sp, color = Color(0xFF64748B))
                                                    }
                                                }
                                            },
                                            onClick = {
                                                selectedStaffId = staff.id
                                                expanded = false
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }

                    // Quick Shift Preset Pills
                    Column {
                        Text("Hızlı Vardiya Şablonu", fontSize = 12.sp, fontWeight = FontWeight.SemiBold, color = Color(0xFF475569))
                        Spacer(Modifier.height(6.dp))
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            quickShifts.forEach { (name, start, end) ->
                                val isSelected = startTime == start && endTime == end
                                Surface(
                                    shape = RoundedCornerShape(8.dp),
                                    color = if (isSelected) Color(0xFFEFF6FF) else Color(0xFFF8FAFC),
                                    border = BorderStroke(1.dp, if (isSelected) Color(0xFF2563EB) else Color(0xFFE2E8F0)),
                                    modifier = Modifier
                                        .weight(1f)
                                        .clickable {
                                            startTime = start
                                            endTime = end
                                        }
                                ) {
                                    Column(
                                        modifier = Modifier.padding(vertical = 8.dp),
                                        horizontalAlignment = Alignment.CenterHorizontally
                                    ) {
                                        Text(name, fontWeight = FontWeight.Bold, fontSize = 12.sp, color = if (isSelected) Color(0xFF2563EB) else Color(0xFF334155))
                                        Text("$start-$end", fontSize = 10.sp, color = if (isSelected) Color(0xFF2563EB) else Color(0xFF64748B))
                                    }
                                }
                            }
                        }
                    }

                    // Start & End Time Inputs
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(10.dp)
                    ) {
                        OutlinedTextField(
                            value = startTime,
                            onValueChange = { startTime = it },
                            label = { Text("Başlangıç (HH:MM)") },
                            shape = RoundedCornerShape(10.dp),
                            modifier = Modifier.weight(1f),
                            singleLine = true,
                            colors = OutlinedTextFieldDefaults.colors(
                                focusedBorderColor = Color(0xFF2563EB),
                                unfocusedBorderColor = Color(0xFFCBD5E1),
                                focusedContainerColor = Color.White,
                                unfocusedContainerColor = Color(0xFFF8FAFC)
                            )
                        )
                        OutlinedTextField(
                            value = endTime,
                            onValueChange = { endTime = it },
                            label = { Text("Bitiş (HH:MM)") },
                            shape = RoundedCornerShape(10.dp),
                            modifier = Modifier.weight(1f),
                            singleLine = true,
                            colors = OutlinedTextFieldDefaults.colors(
                                focusedBorderColor = Color(0xFF2563EB),
                                unfocusedBorderColor = Color(0xFFCBD5E1),
                                focusedContainerColor = Color.White,
                                unfocusedContainerColor = Color(0xFFF8FAFC)
                            )
                        )
                    }

                    // Role / Duty
                    Column {
                        Text("Görev / Departman", fontSize = 12.sp, fontWeight = FontWeight.SemiBold, color = Color(0xFF475569))
                        Spacer(Modifier.height(6.dp))
                        OutlinedTextField(
                            value = role,
                            onValueChange = { role = it },
                            placeholder = { Text("Örn: Resepsiyonist") },
                            shape = RoundedCornerShape(10.dp),
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                            colors = OutlinedTextFieldDefaults.colors(
                                focusedBorderColor = Color(0xFF2563EB),
                                unfocusedBorderColor = Color(0xFFCBD5E1),
                                focusedContainerColor = Color.White,
                                unfocusedContainerColor = Color(0xFFF8FAFC)
                            )
                        )
                        Spacer(Modifier.height(6.dp))
                        Row(
                            modifier = Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()),
                            horizontalArrangement = Arrangement.spacedBy(6.dp)
                        ) {
                            quickRoles.forEach { r ->
                                Surface(
                                    shape = RoundedCornerShape(6.dp),
                                    color = if (role == r) Color(0xFFEFF6FF) else Color(0xFFF1F5F9),
                                    border = BorderStroke(1.dp, if (role == r) Color(0xFF2563EB) else Color(0xFFE2E8F0)),
                                    modifier = Modifier.clickable { role = r }
                                ) {
                                    Text(
                                        text = r,
                                        fontSize = 11.sp,
                                        fontWeight = if (role == r) FontWeight.SemiBold else FontWeight.Normal,
                                        color = if (role == r) Color(0xFF2563EB) else Color(0xFF475569),
                                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                                    )
                                }
                            }
                        }
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        if (selectedStaffId.isNotBlank()) {
                            viewModel.addShift(
                                staffId = selectedStaffId,
                                startTime = "$startTime:00",
                                endTime = "$endTime:00",
                                role = role
                            )
                            showAddDialog = false
                        }
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                    shape = RoundedCornerShape(10.dp),
                    enabled = selectedStaffId.isNotBlank()
                ) {
                    Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(16.dp))
                    Spacer(Modifier.width(6.dp))
                    Text(l10n("Vardiyayı Kaydet"), fontWeight = FontWeight.SemiBold)
                }
            },
            dismissButton = {
                TextButton(
                    onClick = { showAddDialog = false },
                    shape = RoundedCornerShape(10.dp)
                ) {
                    Text(l10n("İptal"), color = Color(0xFF64748B), fontWeight = FontWeight.SemiBold)
                }
            }
        )
    }
}

@Composable
fun ShiftModernCard(shift: ShiftItem) {
    val statusColor = if (shift.isPresent) Color(0xFF10B981) else Color(0xFF64748B)
    val initials = shift.employeeName.split(" ")
        .mapNotNull { it.firstOrNull()?.toString() }
        .take(2)
        .joinToString("")
        .uppercase()

    Card(
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Surface(
                        shape = CircleShape,
                        color = Color(0xFFEFF6FF),
                        modifier = Modifier.size(42.dp)
                    ) {
                        Box(contentAlignment = Alignment.Center) {
                            Text(
                                text = if (initials.isNotBlank()) initials else "P",
                                fontWeight = FontWeight.Bold,
                                fontSize = 14.sp,
                                color = Color(0xFF2563EB)
                            )
                        }
                    }
                    Spacer(modifier = Modifier.width(12.dp))
                    Column {
                        Text(
                            text = shift.employeeName,
                            fontWeight = FontWeight.Bold,
                            fontSize = 15.sp,
                            color = Color(0xFF0F172A)
                        )
                        Spacer(Modifier.height(2.dp))
                        Text(
                            text = shift.role,
                            color = Color(0xFF64748B),
                            fontSize = 12.sp
                        )
                    }
                }

                Surface(
                    shape = RoundedCornerShape(8.dp),
                    color = if (shift.isPresent) Color(0xFFECFDF5) else Color(0xFFF1F5F9),
                    border = BorderStroke(1.dp, if (shift.isPresent) Color(0xFFA7F3D0) else Color(0xFFE2E8F0))
                ) {
                    Row(
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Box(
                            modifier = Modifier
                                .size(6.dp)
                                .clip(CircleShape)
                                .background(statusColor)
                        )
                        Spacer(Modifier.width(6.dp))
                        Text(
                            text = if (shift.isPresent) "Aktif / Planlandı" else "Beklemede",
                            color = if (shift.isPresent) Color(0xFF059669) else Color(0xFF64748B),
                            fontWeight = FontWeight.SemiBold,
                            fontSize = 11.sp
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(12.dp))
            HorizontalDivider(color = Color(0xFFF8FAFC))
            Spacer(modifier = Modifier.height(10.dp))

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Surface(
                        shape = RoundedCornerShape(6.dp),
                        color = Color(0xFFF8FAFC),
                        border = BorderStroke(1.dp, Color(0xFFE2E8F0))
                    ) {
                        Row(
                            modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(Icons.Outlined.Schedule, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(14.dp))
                            Spacer(modifier = Modifier.width(6.dp))
                            Text(
                                text = shift.time,
                                color = Color(0xFF0F172A),
                                fontWeight = FontWeight.SemiBold,
                                fontSize = 13.sp
                            )
                        }
                    }
                }

                Text(
                    text = "Normal Mesai",
                    fontSize = 12.sp,
                    color = Color(0xFF94A3B8)
                )
            }
        }
    }
}

@Composable
fun ShiftBottomNavBar(
    selectedTab: Int = 0,
    onShiftsClick: () -> Unit = {},
    onPersonelClick: () -> Unit = {}
) {
    Surface(
        color = Color.White,
        border = BorderStroke(1.dp, Color(0xFFE2E8F0))
    ) {
        NavigationBar(
            containerColor = Color.White,
            contentColor = Color(0xFF64748B),
            modifier = Modifier.height(64.dp)
        ) {
            NavigationBarItem(
                icon = { Icon(Icons.Outlined.CalendarMonth, contentDescription = l10n("Vardiyalar")) },
                label = { Text(l10n("Vardiyalar"), fontSize = 11.sp, fontWeight = if (selectedTab == 0) FontWeight.Bold else FontWeight.Normal) },
                selected = selectedTab == 0,
                colors = NavigationBarItemDefaults.colors(
                    selectedIconColor = Color(0xFF2563EB),
                    selectedTextColor = Color(0xFF2563EB),
                    indicatorColor = Color(0xFFEFF6FF),
                    unselectedIconColor = Color(0xFF64748B),
                    unselectedTextColor = Color(0xFF64748B)
                ),
                onClick = onShiftsClick
            )
            NavigationBarItem(
                icon = { Icon(Icons.Outlined.People, contentDescription = l10n("Personel Listesi")) },
                label = { Text(l10n("Personel Listesi"), fontSize = 11.sp, fontWeight = if (selectedTab == 1) FontWeight.Bold else FontWeight.Normal) },
                selected = selectedTab == 1,
                colors = NavigationBarItemDefaults.colors(
                    selectedIconColor = Color(0xFF2563EB),
                    selectedTextColor = Color(0xFF2563EB),
                    indicatorColor = Color(0xFFEFF6FF),
                    unselectedIconColor = Color(0xFF64748B),
                    unselectedTextColor = Color(0xFF64748B)
                ),
                onClick = onPersonelClick
            )
        }
    }
}
